import { Link as RouterLink } from 'react-router-dom'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { instrumentInfoPath } from '@/features/instrument-onboarding/lib/instrument-link-routing'
import { RiGiftLine, RiTimerLine } from '@/shared/icons/remix'
import type { AvailableSurveyItem } from '../lib/dummy-home-data'
import {
  homeEarnAmount,
  homeEarnLabel,
  homeStartSurveyButtonSx,
  homeEarnPillSx,
  homePillRowSx,
  homeSurveyCardSx,
  homeSurveyDescriptionSx,
  homeSurveyTitleSx,
  homeTimePillSx,
} from '../styles/homeStyles'

const noRewardLabelSx = {
  fontSize: '14px',
  lineHeight: '20px',
  color: '#6B7280',
  fontStyle: 'italic',
} as const

interface AvailableSurveyCardProps {
  readonly survey: AvailableSurveyItem
  readonly activeResponseId?: string | null
  readonly underReview?: boolean
  readonly onUnderReviewBlocked?: () => void
}

function surveyPrimaryAction({
  survey,
  continueTo,
  startBlocked,
  resumeBlocked,
  onUnderReviewBlocked,
}: {
  survey: AvailableSurveyCardProps['survey']
  continueTo: { pathname: string; state: { responseId: string; instrumentId: string } } | null
  startBlocked: boolean
  resumeBlocked: boolean
  onUnderReviewBlocked?: () => void
}) {
  if (continueTo && !resumeBlocked) {
    return (
      <Button
        component={RouterLink}
        to={continueTo}
        variant="contained"
        color="primary"
        fullWidth
        sx={homeStartSurveyButtonSx}
        data-testid={`home-survey-${survey.id}-resume`}
        aria-label={`Resume survey: ${survey.title}`}
      >
        Resume
      </Button>
    )
  }

  if (startBlocked || resumeBlocked) {
    return (
      <Button
        type="button"
        variant="contained"
        color="primary"
        fullWidth
        sx={homeStartSurveyButtonSx}
        data-testid={
          resumeBlocked
            ? `home-survey-${survey.id}-resume`
            : `home-survey-${survey.id}-start`
        }
        aria-label={
          resumeBlocked
            ? `Resume survey blocked: ${survey.title}`
            : `Start survey blocked: ${survey.title}`
        }
        onClick={() => onUnderReviewBlocked?.()}
      >
        {resumeBlocked ? 'Resume' : 'Start Survey'}
      </Button>
    )
  }

  return (
    <Button
      component={RouterLink}
      to={instrumentInfoPath(survey.id)}
      variant="contained"
      color="primary"
      fullWidth
      sx={homeStartSurveyButtonSx}
      data-testid={`home-survey-${survey.id}-start`}
      aria-label={`Start survey: ${survey.title}`}
    >
      Start Survey
    </Button>
  )
}

export function AvailableSurveyCard({
  survey,
  activeResponseId,
  underReview = false,
  onUnderReviewBlocked,
}: AvailableSurveyCardProps) {
  const continueTo =
    activeResponseId == null
      ? null
      : {
          pathname: `/instrument/${survey.id}/survey`,
          state: { responseId: activeResponseId, instrumentId: survey.id },
        }

  const startBlocked = underReview && !continueTo
  const resumeBlocked = underReview && Boolean(continueTo)
  return (
    <Box sx={homeSurveyCardSx} data-testid={`home-survey-card-${survey.id}`}>
      <Typography component="h3" sx={homeSurveyTitleSx}>
        {survey.title}
      </Typography>
      {survey.description ? (
        <Typography
          sx={{
            ...homeSurveyDescriptionSx,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {survey.description}
        </Typography>
      ) : null}

      <Box sx={homePillRowSx}>
        <Box sx={homeTimePillSx} data-testid={`home-survey-${survey.id}-duration`}>
          <RiTimerLine size={18} aria-hidden />
          <span>{survey.estimatedDuration}</span>
        </Box>
        {survey.rewardEnabled ? (
          <Box sx={homeEarnPillSx} data-testid={`home-survey-${survey.id}-reward`}>
            <RiGiftLine size={22} color={homeEarnLabel} aria-hidden />
            <Typography component="span" sx={{ color: homeEarnLabel, fontWeight: 600 }}>
              Earn{' '}
              <Box component="span" sx={{ fontWeight: 700, color: homeEarnAmount }}>
                {survey.rewardAmount}
              </Box>
            </Typography>
          </Box>
        ) : (
          <Box sx={homeEarnPillSx} data-testid={`home-survey-${survey.id}-no-reward`}>
            <Typography component="span" sx={noRewardLabelSx}>
              No Reward
            </Typography>
          </Box>
        )}
      </Box>

      {surveyPrimaryAction({
        survey,
        continueTo,
        startBlocked,
        resumeBlocked,
        onUnderReviewBlocked,
      })}
    </Box>
  )
}
